import { CheckboxField, Flex, Link } from "@aws-amplify/ui-react"
import { formatUnicorn } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import type { UseFormRegisterReturn } from "react-hook-form"
import { FaRegFilePdf } from "react-icons/fa6"
import { isDev } from "../../config"

interface DataActConsentProps {
  /** Version of the current EU Data Act terms (DNV) */
  currentTermsVersion: string
  /** Link to the current terms */
  currentTermsUrl: string
  /** VIN of the machine, if known already */
  machineSerial?: string | undefined
  /** The form's registration of the "share with my dealer" checkbox */
  shareWithDealer: UseFormRegisterReturn
  /** The form's registration of the "I have read the terms" checkbox */
  acknowledge: UseFormRegisterReturn
}

/**
 * What the user agrees to with the EU Data Act terms (DNV), the option to
 * share with the dealer, the link to the terms, and the "I have read them"
 * checkbox. For forms that accept the terms (`AcceptTermsSchema`); the submit
 * button is the form's.
 */
export function DataActConsent({
  currentTermsVersion,
  currentTermsUrl,
  machineSerial,
  shareWithDealer,
  acknowledge,
}: DataActConsentProps) {
  return (
    <>
      <div className="mb-2">
        {machineSerial
          ? formatUnicorn(
              I18n.get(
                "Wenn Sie der Datennutzung zustimmen, wird die Datenübertragung von der Maschine {machineSerial} aktiviert."
              ),
              { machineSerial }
            )
          : I18n.get(
              "Wenn Sie der Datennutzung zustimmen, wird die Datenübertragung von der Maschine aktiviert."
            )}

        <Flex alignItems="center" gap="xs">
          👍
          {I18n.get(
            "Sie erhalten Wiegedaten, Fütterungsdaten und Rezeptauswertungen, mit denen Sie Ihren Betrieb optimieren können."
          )}
        </Flex>
        <Flex alignItems="center" gap="xs">
          👍
          {I18n.get(
            "Der BSL Kundendienst bekommt Zugriff auf Telemetrie-Daten der Maschine, und kann so bei Problemen schneller helfen."
          )}
        </Flex>
        <Flex alignItems="center" gap="xs">
          👍
          {I18n.get(
            "Optional: Ihr Händler bekommt Zugriff auf Telemetrie-Daten der Maschine, und kann so bei Problemen schneller helfen. Ihre Fütterungsdaten kann er nicht einsehen."
          )}
        </Flex>
      </div>
      {/* Dev only until the dealer's name is known */}
      {isDev && (
        <div className="mb-2">
          <CheckboxField
            label={formatUnicorn(
              I18n.get(
                "Maschinendaten auch für meinen Händler {dealerName} freigeben"
              ),
              { dealerName: "" /** @todo Händlernamen ergänzen */ }
            )}
            {...shareWithDealer}
          />
        </div>
      )}
      <div className="mb-2">
        {I18n.get("Hier können Sie")}{" "}
        <Link href={currentTermsUrl} target="_blank">
          <Flex className="!inline-flex" alignItems="center" gap="xs">
            {formatUnicorn(
              I18n.get(
                "die Datennutzungsvereinbarung (Version {currentTermsVersion})"
              ),
              { currentTermsVersion }
            )}
            <FaRegFilePdf />
          </Flex>
        </Link>{" "}
        {I18n.get("herunterladen und einsehen.")}
      </div>
      <div className="mb-2">
        <CheckboxField
          label={I18n.get("Zur Kenntnis genommen")}
          {...acknowledge}
        />
      </div>
    </>
  )
}
